import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Eye, EyeOff, LogIn } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Account } from './accountType';
import AccountApi from './accountApi';
import { getSessionData } from '../lib/global_function';
import { validateEmail, validateLoginId, validatePasswordMatch, validateAndFormatPhoneNumber, validatePassword } from '../lib/validator';
import HelpIcon from '../components/ui/helpIcon';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import CTASection from '../home/sections/CTA';

const CreateAccount: React.FC = () => {
  const [account, setAccount] = useState<Account>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    company_code: '',
    login_id: '',
    login_password: '',
    auth_provider: '',
    login_password_confirmation: '',
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [companyCode, setcompanyCode] = useState<string>('');
  const [authProvider, setAuthProvider] = useState<string>('');
  const [isCreateSuccess, setIsCreateSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentPhone, setConsentPhone] = useState(false);
  const [consentEmail, setConsentEmail] = useState(false);
  const areAllCheckboxesChecked = termsAccepted && consentPhone && consentEmail;
  const navigate = useNavigate();

  useEffect(() => {
    const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    const companyCode = sessionData?.company_code;

    if (companyCode) {
      setcompanyCode(companyCode);
    } else {
      setcompanyCode('');
      setErrorMessage('It seems that your company data is missing. Online account must be under a valid company.');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAccount(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'phone':
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setAccount(prev => ({ ...prev, phone: phoneResult.value }));
        }
        break;

      case 'email':
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setAccount(prev => ({ ...prev, email: emailResult.value }));
        }
        break;

      case 'login_id':
        const loginIdResult = validateLoginId(value);
        if (!loginIdResult.isValid) {
          errors.push(loginIdResult.error || 'Invalid login ID');
        } else {
          setAccount(prev => ({ ...prev, login_id: loginIdResult.value }));
        }
        break;

      case 'login_password':
        const passwordResult = validatePassword(value);
        if (!passwordResult.isValid) {
          errors.push(passwordResult.error || 'Invalid password');
        } else {
          setAccount(prev => ({ ...prev, login_password: passwordResult.value }));
        }
        break;

      case 'login_password_confirmation':
        const confirmResult = validatePasswordMatch(account.login_password, value);
        if (!confirmResult.isValid) {
          errors.push(confirmResult.error || 'Password not match');
        } else {
          setAccount(prev => ({ ...prev, login_password_confirmation: confirmResult.value }));
        }
        break;
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  };

  const validateForm = (): boolean => {
    setErrorMessage('');

    const phoneResult = validateAndFormatPhoneNumber(account.phone);
    if (!phoneResult.isValid) {
      setErrorMessage(phoneResult.error || 'Invalid phone number');
      return false;
    }

    const emailResult = validateEmail(account.email);
    if (!emailResult.isValid) {
      setErrorMessage(emailResult.error || 'Invalid email address');
      return false;
    }

    const loginIdResult = validateLoginId(account.login_id);
    if (!loginIdResult.isValid) {
      setErrorMessage(loginIdResult.error || 'Invalid login Id');
      return false;
    }

    const passwordResult = validatePassword(account.login_password);
    if (!passwordResult.isValid) {
      setErrorMessage(passwordResult.error || 'Invalid password');
      return false;
    }

    const confirmResult = validatePasswordMatch(account.login_password, account.login_password_confirmation);
    if (!confirmResult.isValid) {
      setErrorMessage(confirmResult.error || 'Password not match');
      return false;
    }

    if (!termsAccepted) {
      setErrorMessage('You must accept the Terms of Use and Privacy Policy to continue.');
      return false;
    }

    if (!consentPhone) {
      setErrorMessage('You must consent to the use of your phone number for communication.');
      return false;
    }

    if (!consentEmail) {
      setErrorMessage('You must consent to the use of your email for communication.');
      return false;
    }

    return true;
  };

  const handleTermsChange = (checked: boolean) => {
    setTermsAccepted(checked);
  };

  const handleConsentPhoneChange = (checked: boolean) => {
    setConsentPhone(checked);
  };

  const handleConsentEmailChange = (checked: boolean) => {
    setConsentEmail(checked);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setErrorMessage('');

    if (!termsAccepted || !consentPhone || !consentEmail) {
      setErrorMessage('You must accept all the terms and consents to continue.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (companyCode.length < 6) {
      setErrorMessage('Your company data is missing or not correct.');
      return;
    }

    try {
      const accountData = { ...account, company_code: companyCode, auth_provider: 'self' };
      const response = await AccountApi.create(accountData);

      const mrjoeData = {
        account_id: response.data.attributes.account_id,
        first_name: response.data.attributes.first_name,
        last_name: response.data.attributes.last_name,
        company_code: companyCode,
      };

      const encodedMrjoeData = btoa(JSON.stringify(mrjoeData));
      sessionStorage.setItem(process.env.REACT_APP_SESSION_STORAGE_KEY as string, encodedMrjoeData);

      setIsCreateSuccess(true);
      setAuthProvider('self');

    } catch (error: unknown) {
      setIsCreateSuccess(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };

  const handleGoogleSuccess = async (response: any) => {
    setErrorMessage('');

    if (!termsAccepted || !consentPhone || !consentEmail) {
      setErrorMessage('You must accept all the terms and consents to continue.');
      return;
    }

    setAuthProvider('google');

    const token = response.credential;
    try {
      const response = await AccountApi.create_google_account(token, companyCode);

      const accountData = {
        account_id: response.data.attributes.account_id,
        first_name: response.data.attributes.first_name,
        last_name: response.data.attributes.last_name,
        company_code: response.data.attributes.company_code,
        company_name: response.data.attributes.company_name,
      };

      const encodedMrjoeData = btoa(JSON.stringify(accountData));
      sessionStorage.setItem(process.env.REACT_APP_SESSION_STORAGE_KEY as string, encodedMrjoeData);
      setIsCreateSuccess(true);

      navigate('/main/customer');
    } catch (error) {
      setIsCreateSuccess(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleGoogleFailure = () => {
    setErrorMessage('Sign in with Google failed');
    setIsCreateSuccess(false);
  };

  const handleGoLogin = () => {
    navigate('/login');
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID || ''}>
      <>
        <section className="page-padding">
          <div className="container-safe">
            <div className="mx-auto max-w-3xl">
              {!companyCode ? (
                <Alert variant="error">
                  <div className="space-y-2">
                  {errorMessage}
                    <p>
                      If you think that this is an issue, please contact us by clicking Contact Us link at the bottom of the page.
                    </p>
                  </div>
                </Alert>
              ) : (
                <div className="space-y-6">
                  {/* Error Message */}
                  {errorMessage && (
                    <Alert variant="error">
                      {errorMessage}
                    </Alert>
                  )}

                  {/* Success View */}
                  {isCreateSuccess ? (
                    <div className="space-y-6">
                      <Alert variant="success">
                        <div className="space-y-3 text-center">
                          <h2 className="text-xl font-semibold">Account Created Successfully!</h2>
                          {authProvider === 'self' && (
                            <>
                              <p className="text-base">Your account has been created.</p>
                              <p className="text-sm">Step 3: Click "Go to Log In" button to log in.</p>
                            </>
                          )}
                          {authProvider === 'google' && (
                            <>
                              <p className="text-base">Your Google account has been linked.</p>
                              <p className="text-sm">Step 3: Please click "Go to Log In" button to log in.</p>
                            </>
                          )}
                        </div>
                      </Alert>

                      <div className="text-center">
                        <Button onClick={handleGoLogin} size="medium" variant="default">
                          <LogIn className="size-5" />
                          Go to Log In
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h1 className="page-heading">Step 2: Create Online Account</h1>

                      <p className="text-base text-paragraph leading-relaxed">
                        There are two options: (1) create your own account, or (2) link your existing Google account. 
                        You can only create an account after checking all three check boxes below.
                      </p>

                      {/* Consent Checkboxes */}
                      <Alert variant="info">
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="terms"
                              checked={termsAccepted}
                              onCheckedChange={handleTermsChange}
                            />
                            <Label htmlFor="terms" className="cursor-pointer font-normal">
                              I agree to the <a href="/pub/terms_of_use" className="text-primary underline hover:text-primary-hover">Terms of Use</a> and <a href="/pub/privacy_policy" className="text-primary underline hover:text-primary-hover">Privacy Policy</a>
                            </Label>
                          </div>

                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="consentPhone"
                              checked={consentPhone}
                              onCheckedChange={handleConsentPhoneChange}
                            />
                            <Label htmlFor="consentPhone" className="cursor-pointer font-normal">
                              I consent to receive SMS messages at the provided phone number for account-related notifications.
                            </Label>
                          </div>

                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="consentEmail"
                              checked={consentEmail}
                              onCheckedChange={handleConsentEmailChange}
                            />
                            <Label htmlFor="consentEmail" className="cursor-pointer font-normal">
                              I consent to receive emails at the provided email address for account-related notifications.
                            </Label>
                          </div>
                        </div>
                      </Alert>

                      {/* Google Sign In Section */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h2 className="form-section-heading">Use Your Google Account</h2>
                        <div 
                          className="flex justify-center"
                          style={{ 
                            opacity: areAllCheckboxesChecked ? 1 : 0.5, 
                            pointerEvents: areAllCheckboxesChecked ? 'auto' : 'none' 
                          }}
                        >
                          <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleFailure}
                            theme="filled_blue"
                            width="300px"
                          />
                        </div>
                      </div>

                      {/* Create Account Form */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h2 className="form-section-heading">Create Your Own Account</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="first_name">
                                First Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                type="text"
                                id="first_name"
                                name="first_name"
                                value={account.first_name}
                                onChange={handleInputChange}
                                required
                              />
                            </div>

                            <div>
                              <Label htmlFor="last_name">
                                Last Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                type="text"
                                id="last_name"
                                name="last_name"
                                value={account.last_name}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="phone">
                              Phone <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={account.phone}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              required
                            />
                          </div>

                          <div>
                            <Label htmlFor="email">
                              Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              type="email"
                              id="email"
                              name="email"
                              value={account.email}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              required
                            />
                          </div>

                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <Label htmlFor="login_id" className="mb-0">
                                Login ID <span className="text-red-500">*</span>
                              </Label>
                              <HelpIcon message="Login ID must be at least 6 characters long" />
                            </div>
                            <Input
                              type="text"
                              id="login_id"
                              name="login_id"
                              value={account.login_id}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              required
                            />
                          </div>

                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <Label htmlFor="login_password" className="mb-0">
                                Password <span className="text-red-500">*</span>
                              </Label>
                              <HelpIcon message="Password must be at least 8 characters long, including both lowercase and uppercase letters, at least one number, and at least one special character" />
                            </div>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                id="login_password"
                                name="login_password"
                                value={account.login_password}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                required
                                className="pr-12"
                              />
                              <button 
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="login_password_confirmation">
                              Password Confirmation <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              type="password"
                              id="login_password_confirmation"
                              name="login_password_confirmation"
                              value={account.login_password_confirmation}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              required
                            />
                          </div>

                          <div>
                            <Button size="medium" variant="default"
                              type="submit"
                              disabled={!areAllCheckboxesChecked}
                            >
                              <UserPlus className="size-5" />
                              Create Account
                            </Button>
                            
                            {!areAllCheckboxesChecked && (
                              <Alert variant="warning">
                                You must accept all the terms and consents to continue.
                              </Alert>
                            )}
                          </div>
                        </form>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
        <CTASection />
      </>
    </GoogleOAuthProvider>
  );
};

export default CreateAccount;